<script setup lang="ts">
import type { FeaturedThisWeekItem } from "~~/server/api/establishments/featured-this-week.get";

/**
 * FeaturedThisWeekV3 — Bloc « À la une » du hero V3 : slider photos + index-card.
 *
 * < lg (mobile + tablette, hero en une colonne) : photo 16:9 pleine largeur +
 *   index-card sobre en overlay haut-droite.
 * ≥ lg (colonne droite du hero) : photo 4:5 inclinée + index-card flottante.
 *
 * Slider :
 * - AUCUNE rotation automatique (EXPERIENCE.md : rien ne bouge à côté de la
 *   recherche) — navigation par flèches (48 px) et swipe uniquement.
 * - SSR toujours item 0 (LCP stable pour Googlebot + first paint).
 * - length === 0 : placeholder paper-100.
 * - length === 1 : pas de nav.
 * - Si la liste raccourcit sous l'index courant, retour à l'item 0.
 */

interface Props {
  featured: FeaturedThisWeekItem[];
}

const props = defineProps<Props>();

const localePath = useLocalePath();
const { t } = useI18n();

// Resolve NuxtLink at runtime so `<component :is>` accepts it as a real Vue component
const NuxtLinkComponent = resolveComponent("NuxtLink");

const currentLink = computed(() => {
  const item = props.featured[currentIndex.value];
  if (!item || !item.departmentCodeSlug) return null;
  return localePath(`/${item.departmentCodeSlug}/${item.slug}`);
});

const currentLinkAriaLabel = computed(() => {
  const item = props.featured[currentIndex.value];
  if (!item) return "";
  return t("pages.home.heroV3.featuredLinkAriaLabel", { name: item.name });
});

const currentIndex = ref(0);
const currentItem = computed<FeaturedThisWeekItem | null>(
  () => props.featured[currentIndex.value] ?? null,
);
const hasMultiple = computed(() => props.featured.length >= 2);
const counterLabel = computed(() => {
  const total = props.featured.length;
  const current = currentIndex.value + 1;
  return `${String(current).padStart(2, "0")} / ${String(total).padStart(2, "0")}`;
});

function goTo(index: number) {
  if (index < 0 || index >= props.featured.length) return;
  currentIndex.value = index;
}

function goPrev() {
  const len = props.featured.length;
  if (len < 2) return;
  goTo((currentIndex.value - 1 + len) % len);
}

function goNext() {
  const len = props.featured.length;
  if (len < 2) return;
  goTo((currentIndex.value + 1) % len);
}

const SWIPE_THRESHOLD = 50;
const SWIPE_AXIS_RATIO = 1.5;
const touchStartX = ref(0);
const touchStartY = ref(0);

function onSwipeStart(e: TouchEvent) {
  const t = e.touches[0];
  if (!t) return;
  touchStartX.value = t.clientX;
  touchStartY.value = t.clientY;
}

function onSwipeEnd(e: TouchEvent) {
  const t = e.changedTouches[0];
  if (!t) return;
  const dx = t.clientX - touchStartX.value;
  const dy = t.clientY - touchStartY.value;
  if (Math.abs(dx) < SWIPE_THRESHOLD) return;
  if (Math.abs(dx) < Math.abs(dy) * SWIPE_AXIS_RATIO) return;
  if (dx < 0) goNext();
  else goPrev();
}

// Le fetch lazy peut changer la liste après le montage : reclamp de l'index
// si la longueur passe sous l'item courant.
watch(
  () => props.featured.length,
  (len) => {
    if (currentIndex.value >= len) currentIndex.value = 0;
  },
);
</script>

<template>
  <div
    role="region"
    aria-roledescription="carousel"
    :aria-label="$t('pages.home.heroV3.slider.ariaLabel')"
  >
    <!-- < lg (mobile + tablette) : photo 16:9 pleine largeur + index-card overlay haut-droite -->
    <div
      class="lg:hidden relative select-none"
      @touchstart.passive="onSwipeStart"
      @touchend.passive="onSwipeEnd"
    >
      <component
        :is="currentLink ? NuxtLinkComponent : 'div'"
        :to="currentLink || undefined"
        :aria-label="currentLink ? currentLinkAriaLabel : undefined"
        class="featured-card-hover relative block w-full aspect-[16/9] rounded-2xl overflow-hidden border hairline bg-paper-100 shadow-sm transition-all duration-300"
        :class="
          currentLink
            ? 'cursor-pointer hover:shadow-xl hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-offset-2 focus-visible:ring-offset-paper-50'
            : ''
        "
      >
        <Transition name="fade" mode="out-in">
          <img
            v-if="currentItem"
            :key="currentItem.id"
            :src="currentItem.heroPhoto.url"
            :alt="
              currentItem.heroPhoto.alt ??
              $t('pages.home.heroV3.featuredPhotoAltFallback')
            "
            class="w-full h-full object-cover"
            loading="eager"
            fetchpriority="high"
          >
          <div
            v-else
            class="w-full h-full bg-gradient-to-br from-paper-100 to-sage-100"
          />
        </Transition>
      </component>

      <!-- Index-card < lg : sous la photo (< sm), flottante dès sm. En 16 px,
           l'overlay masquerait l'essentiel de la photo à 320 px. -->
      <div
        v-if="currentItem"
        class="mt-3 bg-paper-50 border hairline rounded-xl px-4 py-3 shadow-sm sm:mt-0 sm:absolute sm:top-3 sm:right-3 sm:w-[200px] sm:px-3 sm:py-2 sm:shadow-lg sm:rotate-[1.5deg] motion-reduce:rotate-0"
      >
        <p
          class="font-display text-lg font-light leading-tight text-paper-900 mb-0.5"
        >
          {{ currentItem.name }}
        </p>
        <p class="font-sans text-base text-paper-900/70">
          <template v-if="currentItem.ownerFirstName">
            {{
              $t("pages.home.heroV3.featuredHostedBy", {
                firstName: currentItem.ownerFirstName,
              })
            }}
          </template>
          <template v-else>
            {{ $t("pages.home.heroV3.featuredNoHost") }}
          </template>
          <template v-if="currentItem.departmentName">
            · {{ currentItem.departmentName }}
          </template>
        </p>
      </div>

      <!-- Slider nav < lg : flèches 48 px + compteur sous la photo -->
      <div
        v-if="hasMultiple"
        class="mt-3 flex items-center justify-center gap-4"
      >
        <button
          type="button"
          class="inline-flex items-center justify-center w-12 h-12 rounded-lg border-[1.5px] border-sage-500 bg-paper-50 text-paper-900 hover:bg-sage-100 transition cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-700 focus-visible:ring-offset-2 focus-visible:ring-offset-paper-50"
          :aria-label="$t('pages.home.heroV3.slider.prev')"
          @click="goPrev"
        >
          <UIcon name="i-lucide-chevron-left" class="w-4 h-4" />
        </button>
        <span
          class="font-mono text-base text-paper-900/75 tabular-nums tracking-wider"
        >
          {{ counterLabel }}
        </span>
        <button
          type="button"
          class="inline-flex items-center justify-center w-12 h-12 rounded-lg border-[1.5px] border-sage-500 bg-paper-50 text-paper-900 hover:bg-sage-100 transition cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-700 focus-visible:ring-offset-2 focus-visible:ring-offset-paper-50"
          :aria-label="$t('pages.home.heroV3.slider.next')"
          @click="goNext"
        >
          <UIcon name="i-lucide-chevron-right" class="w-4 h-4" />
        </button>
      </div>
    </div>

    <!-- ≥ lg : photo inclinée + index-card flottante -->
    <div class="relative hidden lg:block select-none">
      <!-- Photo principale (grande) avec cross-fade — légèrement inclinée pour déstructurer avec l'index-card -->
      <component
        :is="currentLink ? NuxtLinkComponent : 'div'"
        :to="currentLink || undefined"
        :aria-label="currentLink ? currentLinkAriaLabel : undefined"
        class="featured-card-hover relative block w-[260px] xl:w-[320px] 2xl:w-[380px] aspect-[4/5] rounded-2xl overflow-hidden border hairline bg-paper-100 shadow-xl rotate-[-2deg] motion-reduce:rotate-0 transition-all duration-300 origin-center"
        :class="
          currentLink
            ? 'cursor-pointer hover:rotate-0 hover:scale-[1.02] hover:shadow-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-offset-2 focus-visible:ring-offset-paper-50'
            : ''
        "
      >
        <Transition name="fade" mode="out-in">
          <img
            v-if="currentItem"
            :key="currentItem.id"
            :src="currentItem.heroPhoto.url"
            :alt="
              currentItem.heroPhoto.alt ??
              $t('pages.home.heroV3.featuredPhotoAltFallback')
            "
            class="w-full h-full object-cover"
            loading="eager"
            fetchpriority="high"
          >
          <div
            v-else
            class="w-full h-full bg-gradient-to-br from-paper-100 to-sage-100"
          />
        </Transition>
      </component>

      <!-- Index-card flottante (Fraunces) — contre-rotation pour équilibrer visuellement -->
      <div
        v-if="currentItem"
        class="absolute -top-3 lg:-top-4 xl:-top-6 -right-3 lg:-right-5 xl:-right-8 w-[150px] lg:w-[180px] xl:w-[200px] 2xl:w-[220px] bg-paper-50 border hairline rounded-xl px-3 lg:px-4 xl:px-5 py-2 lg:py-3 xl:py-4 shadow-lg rotate-[2deg] motion-reduce:rotate-0"
      >
        <p
          class="tag items-start text-paper-900/70 mb-2 text-base tracking-[0.08em] before:mt-[0.6em] before:shrink-0"
        >
          {{ $t("pages.home.heroV3.featuredBadge") }}
        </p>
        <p
          class="font-display text-base lg:text-2xl font-light leading-tight text-paper-900 mb-0.5 lg:mb-1"
        >
          {{ currentItem.name }}
        </p>
        <p class="font-sans text-base text-paper-900/70">
          <template v-if="currentItem.ownerFirstName">
            {{
              $t("pages.home.heroV3.featuredHostedBy", {
                firstName: currentItem.ownerFirstName,
              })
            }}
          </template>
          <template v-else>
            {{ $t("pages.home.heroV3.featuredNoHost") }}
          </template>
          <template v-if="currentItem.departmentName">
            · {{ currentItem.departmentName }}
          </template>
        </p>
      </div>

      <!-- Slider nav ≥ lg : sous la photo (débord compensé par le padding bas du hero) -->
      <div
        v-if="hasMultiple"
        class="absolute -bottom-16 left-0 w-[260px] xl:w-[320px] 2xl:w-[380px] flex items-center justify-center gap-4"
      >
        <button
          type="button"
          class="inline-flex items-center justify-center w-12 h-12 rounded-lg border-[1.5px] border-sage-500 bg-paper-50 text-paper-900 hover:bg-sage-100 transition cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-700 focus-visible:ring-offset-2 focus-visible:ring-offset-paper-50"
          :aria-label="$t('pages.home.heroV3.slider.prev')"
          @click="goPrev"
        >
          <UIcon name="i-lucide-chevron-left" class="w-4 h-4" />
        </button>
        <span
          class="font-mono text-base text-paper-900/75 tabular-nums tracking-wider"
        >
          {{ counterLabel }}
        </span>
        <button
          type="button"
          class="inline-flex items-center justify-center w-12 h-12 rounded-lg border-[1.5px] border-sage-500 bg-paper-50 text-paper-900 hover:bg-sage-100 transition cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-700 focus-visible:ring-offset-2 focus-visible:ring-offset-paper-50"
          :aria-label="$t('pages.home.heroV3.slider.next')"
          @click="goNext"
        >
          <UIcon name="i-lucide-chevron-right" class="w-4 h-4" />
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 350ms ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
